import { FileCheck2 } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import type { Parent } from '../lib/types';
import { TermsAgreement } from './AuthPage';
import { useParent } from './ParentContext';

/**
 * "We've updated our Terms" (#589): the Terms of Service or Privacy Policy changed since this parent accepted them (or
 * they never ticked the boxes), and the admin asks existing parents to accept. Shown before anything else.
 */
export function TermsGate({ onDone }: { onDone: (p: Parent) => void }) {
  const { parent, signOut, toast } = useParent();
  const [agreed, setAgreed] = useState(false);
  const [guardian, setGuardian] = useState(false);
  const [busy, setBusy] = useState(false);
  const t = parent.terms;

  async function accept() {
    setBusy(true);
    try {
      await api.post('/api/me/accept-terms', { acceptTerms: true, guardian: true });
      onDone(await api.get<Parent>('/api/me'));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
      setBusy(false);
    }
  }

  return (
    <main className="auth terms-gate">
      <div className="auth-card card stack">
        <FileCheck2 size={40} className="terms-gate-icon" aria-hidden />
        <h1>We’ve updated our Terms</h1>
        <p className="muted center">Please read and accept them to keep using ClassGuardian. Your family’s records and settings are unchanged.</p>
        <div className="terms-gate-links">
          <a href="/terms" target="_blank" rel="noreferrer">
            Read the Terms of Service
          </a>
          <a href="/privacy" target="_blank" rel="noreferrer">
            Read the Privacy Policy
          </a>
          {t && (
            <span className="muted small">
              Version {t.version}
              {t.updatedAt ? ` · updated ${new Date(t.updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
            </span>
          )}
        </div>
        <TermsAgreement agreed={agreed} guardian={guardian} onAgreed={setAgreed} onGuardian={setGuardian} />
        <button className="btn primary big" disabled={busy || !agreed || !guardian} onClick={accept}>
          {busy ? 'One moment…' : 'Accept and continue'}
        </button>
        <button className="link-btn center" onClick={signOut}>
          Sign out
        </button>
      </div>
    </main>
  );
}
